// ============================================================
// <Enthuellung> — der Choreograf des Ladewerks.
//
// Warum nicht schlicht {laedt ? <Skelett/> : <Inhalt/>}:
//   1. Beim Austausch blitzt fuer ein Bild der Seitenhintergrund durch, weil
//      der alte Knoten weg ist, bevor der neue gezeichnet wurde.
//   2. Die Reihenfolge laesst sich nicht steuern — der Browser entscheidet.
//   3. Eine Kreuzblende braucht BEIDE Schichten gleichzeitig im Baum.
// Deshalb liegen Silhouette und Inhalt im SELBEN Rasterfeld uebereinander
// (display:grid, beide auf grid-area 1/1) und werden gegeneinander geblendet.
//
// Die Silhouette wird nach der Blende AUS DEM BAUM GENOMMEN, nicht nur auf
// Opazitaet 0 gesetzt — sonst laufen ihre Schimmerschleifen unsichtbar weiter
// und kosten bei zwanzig Skelettzeilen dauerhaft Bildrate.
//
// WICHTIG: laedt gehoert aus useLadephase. Enthuellung kennt kein
// Verzoegerungstor und keine Mindeststandzeit — die liegen beim Torwaechter,
// damit es nur eine Uhr gibt. Wer laedt roh durchreicht, bekommt Flackern.
// ============================================================
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type TransitionEvent,
} from "react";
import { AUSBLENDEN_MS, EINBLENDEN_MS, stroemVerzoegerung, type Welle } from "../../lib/ladephase";
import "./ladewerk.css";

/** Ein CSS-Objekt, das auch Custom Properties tragen darf. */
type CssVars = CSSProperties & Record<`--${string}`, string | number>;

/**
 * Eine Welle fuer den ganzen Inhaltsblock, oder eine je Kind — als Liste
 * oder als Funktion ueber den Kindindex.
 */
export type WelleAngabe = Welle | readonly Welle[] | ((index: number) => Welle);

export interface EnthuellungProps {
  /** Aus useLadephase: phase !== "ruhe". */
  laedt: boolean;
  /** Die Textur, die waehrend des Ladens im Rasterfeld steht. */
  silhouette: ReactNode;
  /**
   * Welle(n) des Inhalts. Eine einzelne Zahl blendet den ganzen Block; eine
   * Liste oder Funktion vergibt je Kind eine Welle und staffelt innerhalb
   * der Welle. Pro-Kind wird OHNE zusaetzlichen Knoten gearbeitet (die
   * Klasse wandert per cloneElement ins Kind), damit fremde Raster- und
   * Flex-Layouts unangetastet bleiben.
   */
  welle?: WelleAngabe;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  id?: string;
}

/**
 * Verzoegerung je Kind. Gezaehlt wird INNERHALB der Welle, nicht global —
 * sonst bekaeme das erste Bild der dritten Welle die Staffelstufe des
 * zwanzigsten Elements.
 */
function berechneVerzuege(welle: WelleAngabe, anzahl: number): number[] {
  if (typeof welle === "number") return [stroemVerzoegerung(welle, 0)];
  const gezaehlt = new Map<Welle, number>();
  const liste: number[] = [];
  for (let i = 0; i < anzahl; i++) {
    // Annotation ist Pflicht, nicht Zierde: ohne sie wuerde das ?? 1 zu
    // number verbreitern und der Map-Schluessel Welle passte nicht mehr.
    const w: Welle = typeof welle === "function" ? welle(i) : (welle[i] ?? welle[welle.length - 1] ?? 1);
    const n = gezaehlt.get(w) ?? 0;
    gezaehlt.set(w, n + 1);
    liste.push(stroemVerzoegerung(w, n));
  }
  return liste;
}

export function Enthuellung({
  laedt,
  silhouette,
  welle = 1,
  children,
  className = "",
  style,
  id,
}: EnthuellungProps): ReactElement {
  // an = Inhalt sichtbar. Getrennt von laedt, weil der Inhalt erst ein Bild
  // spaeter angeschaltet werden darf (siehe Effekt unten).
  const [an, setAn] = useState(!laedt);
  // Die Silhouette lebt laenger als laedt: sie muss ihre Blende zu Ende
  // fahren, bevor sie aus dem Baum faellt.
  const [silhouetteDa, setSilhouetteDa] = useState(laedt);
  // fertig = die letzte Einblendung ist durch. Erst dann faellt aria-busy.
  const [fertig, setFertig] = useState(!laedt);

  const proKind = typeof welle !== "number";
  const anzahl = Children.count(children);
  const verzuege = berechneVerzuege(welle, anzahl);
  const maxVerzug = verzuege.length ? Math.max(...verzuege) : 0;
  // Die Silhouette blendet mit dem FRUEHESTEN Inhalt gemeinsam los, nicht
  // sofort: sonst klafft zwischen ihrem Ende (Ausblende) und dem Beginn der
  // Einblende ein Loch, in dem beide Schichten auf 0 stehen (bei Welle 3
  // volle 60 ms leere Flaeche). Das Minimum, nicht verzuege[0]: im Pro-Kind-
  // Betrieb darf ein spaeter einsortiertes Kind der ersten Welle nicht vor
  // der Silhouette auftauchen.
  const minVerzug = verzuege.length ? Math.min(...verzuege) : 0;

  useEffect(() => {
    if (laedt) {
      // Harter Schnitt zurueck: der Inhalt springt auf 0 (im CSS steht dort
      // transition:none). NIE in eine Silhouette hinein ueberblenden.
      setAn(false);
      setFertig(false);
      setSilhouetteDa(true);
      return;
    }
    // Zwei Bilder warten: der Inhalt muss erst mit Opazitaet 0 im Baum
    // stehen, sonst hat der Browser keinen Startwert und springt ohne Blende
    // auf 1. Ein einzelnes rAF reicht dafuer nachweislich nicht zuverlaessig.
    let zweites = 0;
    const erstes = requestAnimationFrame(() => {
      zweites = requestAnimationFrame(() => setAn(true));
    });
    return () => {
      cancelAnimationFrame(erstes);
      if (zweites) cancelAnimationFrame(zweites);
    };
  }, [laedt]);

  // Notuhr fuer die Silhouette: transitionend bleibt aus, wenn der Knoten
  // zwischendurch display:none bekommt oder das Blatt im Hintergrund liegt.
  // minVerzug MUSS drinstehen, seit die Ausblende verzoegert startet: sonst
  // schnitte die Uhr bei Welle 3 schon nach 240 ms, also mitten in der erst
  // ab 180 ms laufenden Blende — die Silhouette wuerde sichtbar wegspringen.
  // Die Uhr ist der einzige verlaessliche Ausbauweg (transitionend feuert im
  // Hintergrund-Tab womoeglich nie); ohne sie liefen die Schimmerschleifen
  // der Textur ewig weiter.
  useEffect(() => {
    if (!an || !silhouetteDa) return;
    const uhr = setTimeout(() => setSilhouetteDa(false), minVerzug + AUSBLENDEN_MS + 120);
    return () => clearTimeout(uhr);
  }, [an, silhouetteDa, minVerzug]);

  // aria-busy faellt genau dann, wenn das letzte gestaffelte Element steht.
  useEffect(() => {
    if (!an) return;
    const uhr = setTimeout(() => setFertig(true), maxVerzug + EINBLENDEN_MS);
    return () => clearTimeout(uhr);
  }, [an, maxVerzug]);

  const silhouetteFertig = (e: TransitionEvent<HTMLDivElement>) => {
    // Nur die eigene Opazitaetsblende zaehlt — Blasen aus der Textur ignorieren.
    if (e.target !== e.currentTarget || e.propertyName !== "opacity") return;
    setSilhouetteDa(false);
  };

  const kinder = proKind
    ? Children.map(children, (kind, i) => {
        if (!isValidElement(kind)) return kind;
        const el = kind as ReactElement<{ className?: string; style?: CSSProperties }>;
        const eigen: CssVars = { ...el.props.style, "--lw-verzug": `${verzuege[i] ?? 0}ms` };
        return cloneElement(el, {
          className: `${el.props.className ?? ""} lw-strom`.trim(),
          style: eigen,
        });
      })
    : children;

  const inhaltStil: CssVars = { "--lw-verzug": `${verzuege[0] ?? 0}ms` };
  // Nur der Startzeitpunkt wandert mit; Dauer und Kurve der Ausblende bleiben
  // im CSS. Der reduced-motion-Block schlaegt diesen Inline-Wert, weil dort
  // !important steht — Autoren-!important gewinnt gegen inline ohne.
  const silhouetteStil: CssVars = { "--lw-weg-verzug": `${minVerzug}ms` };

  return (
    <div
      id={id}
      className={`lw-enthuellung ${className}`.trim()}
      style={style}
      aria-busy={fertig ? undefined : true}
    >
      {silhouetteDa && (
        <div
          className="lw-silhouette"
          data-weg={an ? "true" : undefined}
          style={silhouetteStil}
          onTransitionEnd={silhouetteFertig}
          aria-hidden="true"
        >
          {silhouette}
        </div>
      )}
      <div
        className="lw-inhalt"
        data-an={an ? "true" : "false"}
        data-strom={proKind ? "true" : undefined}
        style={inhaltStil}
        inert={an ? undefined : true}
      >
        {kinder}
      </div>
    </div>
  );
}
